/**
 * Global sonner toast behaviour patch.
 *
 * Why: sonner has no per-type default duration or close-button — these must
 * be set per-call-site. Last Bookmark has 50+ `toast.*` call sites across
 * the app, so instead of touching every file we patch the `toast` methods
 * once at import time. All existing `import { toast } from "sonner"`
 * imports pick up the patched behaviour automatically because sonner's
 * default export is a shared singleton.
 *
 * Behaviour:
 *   - toast.success  — auto-dismiss in ~3s     (ordinary confirmation)
 *   - toast.info     — auto-dismiss in ~4s     (neutral info)
 *   - toast.message  — auto-dismiss in ~4s     (plain message)
 *   - toast.warning  — auto-dismiss in ~6s     (needs attention)
 *   - toast.error    — stays ~10s or until the user closes it (readable)
 *
 * Every type gets a visible close (X) button so a user can dismiss any
 * toast immediately without waiting for the timer. Any explicit per-call
 * option (`toast.error(msg, { duration: 2000 })`) still wins because the
 * per-call `opts` object is spread AFTER the defaults.
 * Toasts carrying an `action` button stay until handled or closed.
 */
import { toast } from "sonner";

const DEFAULTS = {
  success: { duration: 3000, closeButton: true },
  info: { duration: 4000, closeButton: true },
  message: { duration: 4000, closeButton: true },
  warning: { duration: 6000, closeButton: true },
  error: { duration: 10000, closeButton: true },
  loading: { closeButton: true },
};

for (const [method, defaults] of Object.entries(DEFAULTS)) {
  const original = toast[method];
  if (typeof original === "function" && !original.__lb_patched) {
    const patched = (message, opts) => {
      const o = opts || {};
      const sticky = o.action && o.duration === undefined ? { duration: Infinity } : {};
      return original(message, { ...defaults, ...sticky, ...o });
    };
    patched.__lb_patched = true;
    toast[method] = patched;
  }
}

// Dev-friendly window hook — makes it possible to fire toasts from the
// browser console or from Playwright scripts without having to recreate
// the import graph. The hook is a reference to the already-patched
// singleton; it does not duplicate state or create a parallel toast
// stack. Safe to leave in production — it's just one object reference.
if (typeof window !== "undefined") {
  window.__lb_toast = toast;
}

// Also patch the bare `toast(message)` default call so unqualified toasts
// get an auto-dismiss timer and a close button. Only patch if the module
// exposes toast as a callable (sonner does).
if (typeof toast === "function" && !toast.__lb_patched_default) {
  // Can't actually overwrite the function import; this just records that
  // the default call will keep sonner's own default of 4000ms + no close
  // button. Prefer toast.success/info/warning/error at call sites.
  toast.__lb_patched_default = true;
}
